import { setOrDelete } from "./setOrDelete";

// Os cinco campos que mudam de mercado para mercado. Os outros quatro que a copy
// tem — headerTitle, botName, botAvatarUrl, pageContexts — são a persona e as
// regras de URL, iguais em todos os mercados que o widget serve, e por isso nunca
// descem para dentro de uma tradução. É a mesma lista do CopyTranslation do
// backend (ai-agent-backend, src/app/schemas/widget_config.py).
const TRANSLATABLE = [
  "launcherTitle",
  "welcomeTitle",
  "welcomeDescription",
  "welcomeQuickReplies",
  "priceInfoNotice",
];

// Trocar o idioma por omissão de A para B, com tudo o que isso arrasta atrás.
//
// O que o operador quer dizer ao mexer naquele dropdown é "o meu mercado
// principal passa a ser o B"; o que NÃO quer dizer é "deixo de servir o A". Mas o
// A vivia só no `locale` — nunca esteve nos `locales`, porque o conjunto servido é
// `{locale} ∪ locales` e o por omissão já lá estava por construção. Assim que o B
// toma o lugar dele, nada segura o A e o mercado desaparece do widget sem aviso.
//
// E a copy tem de ir com ele, porque é a POSIÇÃO que diz de quem é o texto: os
// campos de topo são, por definição, a copy do idioma por omissão, e o
// `served_copy` do backend lê-os assim — nunca funde a tradução sobre a base e
// ignora de propósito o `translations[por omissão]`. Mudar só o `locale` deixava o
// português no topo com o espanhol por omissão, e um visitante espanhol passava a
// receber português enquanto o `translations.es`, esse sim escrito para ele,
// ficava a ser ignorado. É exactamente a falha que esta feature existe para
// eliminar, a entrar pela porta do lado.
//
// Portanto, de uma vez só:
//   - o `locale` passa a B;
//   - o B sai dos `locales` e o A entra NO LUGAR DELE (e no fim, se o B ainda não
//     era servido). A troca em posição é o que faz A → B → A voltar ao princípio;
//     acrescentar o A no fim não voltava;
//   - os cinco campos de topo descem para `translations[A]` e o
//     `translations[B]` sobe para o topo, saindo do mapa.
//
// Se o B não tinha tradução nenhuma — ou tinha uma entrada só com nulos, que é o
// que o GET devolve para um idioma por preencher — o topo fica VAZIO, e não com o
// texto do A lá dentro. É a parte que mais parece um esquecimento e é a que conta:
// sem copy escrita, o widget cai para o genérico do próprio B; com as palavras do
// A lá, serve-as como se fossem dele.
//
// Vazio quer dizer CHAVE AUSENTE e não chave a vazio, aqui como em todo o resto da
// pasta (ver setOrDelete): é a ausência que a resolução do widget lê como "não
// configurado". Um idioma cuja entrada fique sem campos sai do mapa, e o mapa sai
// da copy quando fica sem idiomas.
//
// Recebe e devolve o `formValues` inteiro porque a transacção precisa dos três ao
// mesmo tempo — `locale`, `locales` e `copy` —, e é por isso que vive aqui e não
// no LanguageSection, que só tem os dois primeiros.
//
// Devolve um objecto novo e não toca no que recebe: as entradas do `translations`
// que a troca não mexe viajam por referência para o resultado, e escrever dentro
// de uma delas era escrever no estado anterior do React.
export function swapDefaultLocale(formValues, next) {
  const current = formValues.locale || "pt";
  if (next === current) return formValues;

  const copy = formValues.copy || {};
  const translations = { ...(copy.translations || {}) };
  const promoted = translations[next] || {};
  delete translations[next];

  // O `?? null` não é enfeite: o setOrDelete trata do null e do vazio, mas um
  // `undefined` — o que dá ler um campo que não está lá — passava-lhe pela malha e
  // ficava como chave presente com valor undefined. O JSON.stringify não a mostra,
  // mas o Object.keys conta-a, e um idioma "vazio" assim nunca saía do mapa.
  const demoted = TRANSLATABLE.reduce((acc, f) => setOrDelete(acc, f, copy[f] ?? null), {});
  // O que desce é o TOPO, e não o que o `translations[A]` porventura tivesse: para
  // o A, era o topo que estava a ser servido, e uma entrada dele no mapa era letra
  // morta que o backend ignorava. Se o topo estava vazio, o A fica sem copy — que
  // é o que ele tinha.
  if (Object.keys(demoted).length) translations[current] = demoted;
  else delete translations[current];

  let nextCopy = TRANSLATABLE.reduce((acc, f) => setOrDelete(acc, f, promoted[f] ?? null), copy);
  nextCopy = setOrDelete(
    nextCopy,
    "translations",
    Object.keys(translations).length ? translations : null
  );

  const others = (formValues.locales || []).map((l) => (l === next ? current : l));
  if (!others.includes(current)) others.push(current);

  return {
    ...formValues,
    locale: next,
    // O Set porque um `locales` que já trouxesse o por omissão lá dentro — o
    // LanguageSection filtra-o para desenhar, e a base pode tê-lo — ficava com o
    // mesmo idioma duas vezes depois da troca em posição.
    locales: [...new Set(others)],
    copy: nextCopy,
  };
}
